import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  HourglassEmpty as WaitlistIcon,
  PlayArrow as StartReviewIcon,
  Info as InfoIcon,
  Payment as PaymentIcon,
  Done as CompleteIcon,
  Person as PersonIcon,
  Group as FamilyIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import conversionService from '../api/services/conversionService';
import {
  ConversionApplication,
  ConversionPreview,
} from '../types';

const statusSteps = [
  'Draft',
  'Submitted',
  'Under Review',
  'Decision',
  'Payment',
  'Converted',
];

const getStatusStep = (status: string): number => {
  switch (status?.toUpperCase()) {
    case 'DRAFT':
      return 0;
    case 'SUBMITTED':
      return 1;
    case 'UNDER_REVIEW':
    case 'ADDITIONAL_INFO':
      return 2;
    case 'APPROVED':
    case 'REJECTED':
    case 'WAITLISTED':
      return 3;
    case 'PAYMENT_PENDING':
      return 4;
    case 'CONVERTED':
      return 5;
    case 'WITHDRAWN':
      return -1;
    default:
      return 0;
  }
};

const ConversionApplicationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [application, setApplication] = useState<ConversionApplication | null>(null);
  const [preview, setPreview] = useState<ConversionPreview | null>(null);

  // Dialog states
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [waitlistDialogOpen, setWaitlistDialogOpen] = useState(false);
  const [waitlistPosition, setWaitlistPosition] = useState<number | ''>('');
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [initiationFee, setInitiationFee] = useState<number | ''>('');
  const [firstDues, setFirstDues] = useState<number | ''>('');
  const [paymentDueDate, setPaymentDueDate] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoNotes, setInfoNotes] = useState('');
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [converting, setConverting] = useState(false);

  // Load application
  const loadApplication = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await conversionService.getById(id);
      setApplication(response.data);

      // Load preview if ready for conversion
      if (['APPROVED', 'PAYMENT_PENDING'].includes(response.data.status?.toUpperCase() || '')) {
        try {
          const previewResponse = await conversionService.previewConversion(id);
          setPreview(previewResponse.data);
        } catch (err) {
          console.log('Preview not available yet');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load application');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadApplication();
  }, [loadApplication]);

  // Clear alerts
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Actions
  const handleStartReview = async () => {
    if (!id) return;
    try {
      await conversionService.startReview(id);
      setSuccess('Review started');
      loadApplication();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start review');
    }
  };

  const handleRequestInfo = async () => {
    if (!id) return;
    try {
      await conversionService.requestAdditionalInfo(id, infoNotes);
      setSuccess('Additional information requested');
      setInfoDialogOpen(false);
      setInfoNotes('');
      loadApplication();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to request information');
    }
  };

  const handleApprove = async () => {
    if (!id) return;
    try {
      await conversionService.approve(id, {
        initiation_fee: initiationFee || undefined,
        first_dues: firstDues || undefined,
        payment_due_date: paymentDueDate || undefined,
        notes: approvalNotes || undefined,
      });
      setSuccess('Application approved');
      setApproveDialogOpen(false);
      loadApplication();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve application');
    }
  };

  const handleReject = async () => {
    if (!id) return;
    try {
      await conversionService.reject(id, rejectReason);
      setSuccess('Application rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      loadApplication();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reject application');
    }
  };

  const handleWaitlist = async () => {
    if (!id) return;
    try {
      await conversionService.addToWaitlist(id, waitlistPosition || undefined);
      setSuccess('Added to waitlist');
      setWaitlistDialogOpen(false);
      setWaitlistPosition('');
      loadApplication();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add to waitlist');
    }
  };

  const handleRecordPayment = async () => {
    if (!id) return;
    try {
      await conversionService.recordPayment(id);
      setSuccess('Payment recorded');
      loadApplication();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to record payment');
    }
  };

  const handleConvert = async () => {
    if (!id) return;
    setConverting(true);
    try {
      const result = await conversionService.completeConversion(id);
      setSuccess(`Successfully converted to member ${result.data.member.member_number}`);
      setConvertDialogOpen(false);
      loadApplication();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to complete conversion');
    } finally {
      setConverting(false);
    }
  };

  // Get status color
  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status?.toUpperCase()) {
      case 'CONVERTED':
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
      case 'WITHDRAWN':
        return 'error';
      case 'UNDER_REVIEW':
      case 'WAITLISTED':
        return 'warning';
      case 'SUBMITTED':
      case 'PAYMENT_PENDING':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!application) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Application not found</Alert>
      </Container>
    );
  }

  const currentStep = getStatusStep(application.status as string);
  const canStartReview = application.status?.toUpperCase() === 'SUBMITTED';
  const canMakeDecision = application.status?.toUpperCase() === 'UNDER_REVIEW';
  const canRecordPayment = application.status?.toUpperCase() === 'APPROVED';
  const canConvert = ['APPROVED', 'PAYMENT_PENDING'].includes(application.status?.toUpperCase() || '');
  const isComplete = application.status?.toUpperCase() === 'CONVERTED';
  const isTerminal = ['CONVERTED', 'REJECTED', 'WITHDRAWN'].includes(application.status?.toUpperCase() || '');

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Button
        startIcon={<BackIcon />}
        onClick={() => navigate('/non-member-management')}
        sx={{ mb: 2 }}
      >
        Back to Management
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Application #{application.application_number}</Typography>
          <Typography variant="body1" color="textSecondary">
            {application.applicant?.first_name} {application.applicant?.last_name} - {application.applicant?.email}
          </Typography>
        </Box>
        <Chip
          label={application.status?.replace(/_/g, ' ')}
          color={getStatusColor(application.status as string)}
          size="medium"
        />
      </Box>

      {/* Progress Stepper */}
      {!isTerminal && currentStep >= 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={currentStep} alternativeLabel>
            {statusSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      )}

      <Grid container spacing={3}>
        {/* Application Details */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Application Details</Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">Requested Tier</Typography>
                  <Typography variant="body1">{application.requested_tier?.tier_name || '-'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">Requested Member Type</Typography>
                  <Typography variant="body1">{application.requested_member_type || 'PRIMARY'}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">Submitted At</Typography>
                  <Typography variant="body1">
                    {application.submitted_at ? new Date(application.submitted_at).toLocaleString() : 'Not submitted'}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="textSecondary">Created At</Typography>
                  <Typography variant="body1">
                    {new Date(application.created_at).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>

              {/* Sponsors */}
              {(application.sponsor1_member_id || application.sponsor2_member_id) && (
                <>
                  <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Sponsors</Typography>
                  <Grid container spacing={2}>
                    {application.sponsor1 && (
                      <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="textSecondary">Sponsor 1</Typography>
                          <Typography variant="body1">
                            {application.sponsor1.first_name} {application.sponsor1.last_name}
                          </Typography>
                          <Chip
                            size="small"
                            label={application.sponsor1_approved ? 'Approved' : 'Pending'}
                            color={application.sponsor1_approved ? 'success' : 'default'}
                            sx={{ mt: 1 }}
                          />
                        </Paper>
                      </Grid>
                    )}
                    {application.sponsor2 && (
                      <Grid size={{ xs: 6 }}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                          <Typography variant="body2" color="textSecondary">Sponsor 2</Typography>
                          <Typography variant="body1">
                            {application.sponsor2.first_name} {application.sponsor2.last_name}
                          </Typography>
                          <Chip
                            size="small"
                            label={application.sponsor2_approved ? 'Approved' : 'Pending'}
                            color={application.sponsor2_approved ? 'success' : 'default'}
                            sx={{ mt: 1 }}
                          />
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}

              {/* Review Notes */}
              {application.review_notes && (
                <>
                  <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Review Notes</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2">{application.review_notes}</Typography>
                  </Paper>
                </>
              )}

              {/* Rejection Reason */}
              {application.rejection_reason && (
                <>
                  <Typography variant="h6" sx={{ mt: 3, mb: 1 }} color="error">Rejection Reason</Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'error.50' }}>
                    <Typography variant="body2">{application.rejection_reason}</Typography>
                  </Paper>
                </>
              )}
            </CardContent>
          </Card>

          {/* Conversion Preview */}
          {preview && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Conversion Preview</Typography>
                <Divider sx={{ mb: 2 }} />

                <List>
                  <ListItem>
                    <ListItemIcon><PersonIcon /></ListItemIcon>
                    <ListItemText
                      primary={`${preview.applicant.first_name} ${preview.applicant.last_name}`}
                      secondary={`Will become: ${preview.applicant.will_become_type} | Member #: ${preview.preview_member_number}`}
                    />
                  </ListItem>
                  {preview.family_members_to_convert.map((fm, index) => (
                    <ListItem key={index}>
                      <ListItemIcon><FamilyIcon /></ListItemIcon>
                      <ListItemText
                        primary={fm.name}
                        secondary={`${fm.relationship} | Sub #: ${fm.preview_sub_number}`}
                      />
                      {fm.already_converted && (
                        <Chip size="small" label="Already Converted" color="default" />
                      )}
                    </ListItem>
                  ))}
                </List>

                {preview.payment_info && (
                  <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Payment Information</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="textSecondary">Initiation Fee</Typography>
                        <Typography variant="body1">
                          ${preview.payment_info.initiation_fee?.toLocaleString() || '0'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="textSecondary">First Dues</Typography>
                        <Typography variant="body1">
                          ${preview.payment_info.first_dues?.toLocaleString() || '0'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="textSecondary">Payment Status</Typography>
                        <Chip
                          size="small"
                          label={preview.payment_info.payment_received ? 'Received' : 'Pending'}
                          color={preview.payment_info.payment_received ? 'success' : 'warning'}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                )}
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Actions Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Actions</Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {canStartReview && (
                  <Button
                    variant="contained"
                    startIcon={<StartReviewIcon />}
                    onClick={handleStartReview}
                    fullWidth
                  >
                    Start Review
                  </Button>
                )}

                {canMakeDecision && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<InfoIcon />}
                      onClick={() => setInfoDialogOpen(true)}
                      fullWidth
                    >
                      Request More Info
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<ApproveIcon />}
                      onClick={() => setApproveDialogOpen(true)}
                      fullWidth
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<WaitlistIcon />}
                      onClick={() => setWaitlistDialogOpen(true)}
                      fullWidth
                    >
                      Add to Waitlist
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<RejectIcon />}
                      onClick={() => setRejectDialogOpen(true)}
                      fullWidth
                    >
                      Reject
                    </Button>
                  </>
                )}

                {canRecordPayment && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PaymentIcon />}
                    onClick={handleRecordPayment}
                    fullWidth
                  >
                    Record Payment
                  </Button>
                )}

                {canConvert && (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CompleteIcon />}
                    onClick={() => setConvertDialogOpen(true)}
                    fullWidth
                    size="large"
                  >
                    Complete Conversion
                  </Button>
                )}

                {isComplete && (
                  <Alert severity="success">
                    Conversion completed!
                    {application.new_member_number && (
                      <Typography variant="body2">
                        New Member #: <strong>{application.new_member_number}</strong>
                      </Typography>
                    )}
                  </Alert>
                )}

                {application.status?.toUpperCase() === 'WAITLISTED' && (
                  <Alert severity="info">
                    Position #{application.waitlist_position} on waitlist
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Payment Info Card */}
          {(application.initiation_fee_amount || application.first_dues_amount) && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Payment Details</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={1}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="textSecondary">Initiation Fee</Typography>
                    <Typography variant="body1">${application.initiation_fee_amount?.toLocaleString()}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="textSecondary">First Dues</Typography>
                    <Typography variant="body1">${application.first_dues_amount?.toLocaleString()}</Typography>
                  </Grid>
                  {application.payment_due_date && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="textSecondary">Due Date</Typography>
                      <Typography variant="body1">
                        {new Date(application.payment_due_date).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  )}
                  {application.payment_received_date && (
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="body2" color="textSecondary">Payment Received</Typography>
                      <Typography variant="body1">
                        {new Date(application.payment_received_date).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Request Info Dialog */}
      <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Additional Information</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="What information is needed?"
            value={infoNotes}
            onChange={(e) => setInfoNotes(e.target.value)}
            multiline
            rows={4}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRequestInfo} variant="contained" disabled={!infoNotes}>
            Send Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Application</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Initiation Fee"
                type="number"
                value={initiationFee}
                onChange={(e) => setInitiationFee(e.target.value ? parseFloat(e.target.value) : '')}
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="First Dues"
                type="number"
                value={firstDues}
                onChange={(e) => setFirstDues(e.target.value ? parseFloat(e.target.value) : '')}
                InputProps={{ startAdornment: '$' }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Payment Due Date"
                type="date"
                value={paymentDueDate}
                onChange={(e) => setPaymentDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Approval Notes"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Application</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason for Rejection"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            multiline
            rows={4}
            sx={{ mt: 1 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={!rejectReason}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Waitlist Dialog */}
      <Dialog open={waitlistDialogOpen} onClose={() => setWaitlistDialogOpen(false)}>
        <DialogTitle>Add to Waitlist</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Waitlist Position (optional)"
            type="number"
            value={waitlistPosition}
            onChange={(e) => setWaitlistPosition(e.target.value ? parseInt(e.target.value) : '')}
            sx={{ mt: 1 }}
            helperText="Leave empty to add to end of waitlist"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaitlistDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleWaitlist} variant="contained" color="warning">
            Add to Waitlist
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Complete Conversion</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This will convert the non-member to a full member.
          </Alert>
          {preview && (
            <Box>
              <Typography variant="body2" gutterBottom>
                <strong>New Member Number:</strong> {preview.preview_member_number}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Family Members:</strong> {preview.family_member_count}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)} disabled={converting}>
            Cancel
          </Button>
          <Button
            onClick={handleConvert}
            variant="contained"
            color="success"
            disabled={converting}
            startIcon={converting ? <CircularProgress size={20} /> : <CompleteIcon />}
          >
            {converting ? 'Converting...' : 'Complete Conversion'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ConversionApplicationPage;
